// ABOUTME: Confirmation dialog shown before saving config changes.
// ABOUTME: Lists all pending changes with before/after values and affected product counts.

import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import { colors } from '../../theme/colors';
import { getLevelLabel, getLevelColor } from './LevelBadge';

export default function SaveConfirmationDialog({
  open,
  onClose,
  onConfirm,
  pendingChanges,
  affectedCells,
  saving,
}) {
  const { t } = useTranslation();

  const getAffectedCount = (change) => {
    return affectedCells.filter(a => a.field === change.field).length;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '12px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: colors.text.primary }}>
          {t('config.confirm.title')}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Typography sx={{ fontSize: '0.85rem', color: colors.text.secondary, mb: 2 }}>
          {t('config.confirm.description', { count: pendingChanges.length })}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {pendingChanges.map((change, idx) => (
            <Box key={idx} sx={{
              borderLeft: `3px solid ${getLevelColor(change.level)}`,
              pl: 1.5,
              py: 0.5,
            }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                {t(`config.fields.${change.field}`)} = <strong>{formatVal(change.value)}</strong>
                <Typography component="span" sx={{ fontSize: '0.8rem', color: colors.text.muted, ml: 1 }}>
                  ({t('config.confirm.wasValue', { value: formatVal(change.oldValue) })})
                </Typography>
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: colors.text.secondary }}>
                {t('config.edit.setAtLevel')}: {getLevelLabel(change.level, t)}
                {change.entityId && ` (${change.entityId})`}
              </Typography>
              {getAffectedCount(change) > 1 && (
                <Typography sx={{ fontSize: '0.7rem', color: colors.text.muted }}>
                  {t('config.confirm.affects', { count: getAffectedCount(change) })}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
          {t('config.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={saving}
          sx={{
            textTransform: 'none',
            bgcolor: colors.ui.button,
            borderRadius: '8px',
            '&:hover': { bgcolor: colors.ui.buttonHover },
          }}
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : t('config.confirm.saveAndReload')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function formatVal(val) {
  if (val === null || val === undefined) return '-';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  return String(val);
}
